import React from 'react';
import { Controller, FieldPath, Control } from 'react-hook-form';

import { Field, FieldContent, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PhoneInput } from './PhoneInput';
import { CountryInput } from './CountryInput';
import { Textarea } from '@/components/ui/textarea';
import i18n from '@/utils/i18n';
import { useOrderFormContext } from '../OrderFormContext';
import { cn } from '@/lib/utils';
import { DatePicker } from './DatePicker';
import moment from 'moment';
import { BUSINESS_TYPE_VALUES, OrderFormValues } from '../schema';

export function OrderField() {
  return null;
}

OrderField.BusinessType = function BusinessType({
  className,
}: {
  className?: string;
}) {
  const { formControl } = useOrderFormContext();
  return (
    <Field className={className}>
      <FieldContent>
        <FieldLabel className="tw:text-ellipsis tw:text-nowrap">
          {i18n.t('EventOrder:legalForm')}
        </FieldLabel>
        <Controller
          control={formControl}
          name={'businessType'}
          render={({ field, fieldState }) => {
            const invalid = !field.value || fieldState.invalid;
            return (
              <Select
                name={field.name}
                value={field.value ?? ''}
                onValueChange={field.onChange}
                disabled={field.disabled}
              >
                <SelectTrigger
                  aria-invalid={invalid}
                  className="tw:shadow-xs tw:whitespace-normal tw:md:whitespace-nowrap tw:text-start! tw:bg-input/30"
                >
                  <SelectValue placeholder={i18n.t('Common:select')} />
                </SelectTrigger>
                <SelectContent className="tw:max-w-[320px]">
                  {Object.values(BUSINESS_TYPE_VALUES).map((ct) => (
                    <SelectItem
                      key={ct.value}
                      value={ct.value}
                      className="tw:whitespace-normal"
                    >
                      {ct.getLabel()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            );
          }}
        />
      </FieldContent>
    </Field>
  );
};

OrderField.FirstName = function FirstName({
  name = 'firstName',
  label = i18n.t('EventOrder:firstName'),
  placeholder = i18n.t('EventOrder:firstName'),
  className,
  inputClassName,
}: {
  name?: FieldPath<OrderFormValues>;
  label?: string;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
}) {
  const { formControl } = useOrderFormContext();
  return (
    <Field className={className}>
      <FieldContent>
        <FieldLabel className="tw:text-ellipsis tw:text-nowrap">
          {label}
        </FieldLabel>
        <Controller
          control={formControl}
          name={name}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              className={cn('tw:bg-white tw:text-sm', inputClassName)}
              value={field.value as string}
              placeholder={placeholder}
              aria-invalid={!!fieldState.error}
            />
          )}
        />
      </FieldContent>
    </Field>
  );
};

OrderField.LastName = function LastName({
  name = 'lastName',
  label = i18n.t('EventOrder:lastName'),
  placeholder = i18n.t('EventOrder:lastName'),
  className,
  inputClassName,
}: {
  name?: FieldPath<OrderFormValues>;
  label?: string;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
}) {
  const { formControl } = useOrderFormContext();
  return (
    <Field className={className}>
      <FieldContent>
        <FieldLabel className="tw:text-ellipsis tw:text-nowrap">
          {label}
        </FieldLabel>
        <Controller
          control={formControl}
          name={name}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              value={field.value as string}
              placeholder={placeholder}
              aria-invalid={!!fieldState.error}
              className={cn('tw:bg-white tw:text-sm', inputClassName)}
            />
          )}
        />
      </FieldContent>
    </Field>
  );
};

OrderField.CompanyName = function CompanyName({
  className,
  inputClassName,
}: {
  className?: string;
  inputClassName?: string;
}) {
  const { formControl } = useOrderFormContext();
  return (
    <Field className={className}>
      <FieldContent>
        <FieldLabel className="tw:text-ellipsis tw:text-nowrap">
          {i18n.t('EventOrder:companyName')}
        </FieldLabel>
        <Controller
          control={formControl}
          name={'companyName'}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              placeholder={i18n.t('EventOrder:companyNamePlaceholder')}
              aria-invalid={!!fieldState.error}
              className={cn('tw:bg-white tw:text-sm', inputClassName)}
            />
          )}
        />
      </FieldContent>
    </Field>
  );
};

OrderField.Email = function Email({
  label = i18n.t('EventOrder:email'),
  placeholder = i18n.t('EventOrder:email'),
  className,
  inputClassName,
}: {
  label?: string;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
}) {
  const { formControl } = useOrderFormContext();
  return (
    <Field className={className}>
      <FieldContent>
        <FieldLabel className="tw:text-ellipsis tw:text-nowrap">
          {label}
        </FieldLabel>
        <Controller
          control={formControl}
          name={'email'}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              type="email"
              placeholder={placeholder}
              aria-invalid={!!fieldState.error}
              className={cn('tw:bg-white tw:text-sm', inputClassName)}
            />
          )}
        />
      </FieldContent>
    </Field>
  );
};

OrderField.PhoneNumber = function PhoneNumber({
  label = i18n.t('EventOrder:phoneNumber'),
  placeholder = i18n.t('EventOrder:phoneNumber'),
  className,
  inputClassName,
}: {
  label?: string;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
}) {
  const { formControl } = useOrderFormContext();
  return (
    <Field className={className}>
      <FieldContent>
        <FieldLabel className="tw:text-ellipsis tw:text-nowrap">
          {label}
        </FieldLabel>
        <Controller
          control={formControl}
          name={'phoneNumber'}
          render={({ field, fieldState }) => (
            <div className="tw:flex tw:flex-1 tw:flex-row tw:items-center">
              <PhoneInput
                {...field}
                defaultCountry="DE"
                international={false}
                placeholder={placeholder}
                aria-invalid={!!fieldState.error}
                className={cn(
                  'tw:flex tw:flex-1 tw:flex-row tw:items-center tw:text-sm',
                  inputClassName,
                )}
              />
            </div>
          )}
        />
      </FieldContent>
    </Field>
  );
};

OrderField.VatId = function VatId({
  className,
  inputClassName,
}: {
  className?: string;
  inputClassName?: string;
}) {
  const { formControl } = useOrderFormContext();
  return (
    <Field className={className}>
      <FieldContent>
        <FieldLabel className="tw:text-ellipsis tw:text-nowrap">
          {i18n.t('EventOrder:vatId')}
        </FieldLabel>
        <Controller
          control={formControl}
          name={'vatId'}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              placeholder={i18n.t('EventOrder:enterVatId')}
              aria-invalid={!!fieldState.error}
              className={cn('tw:bg-white tw:text-sm', inputClassName)}
            />
          )}
        />
      </FieldContent>
    </Field>
  );
};

OrderField.DateOfBirth = function DateOfBirth({
  name = 'dateOfBirth',
  label = i18n.t('EventOrder:dateOfBirth'),
  className,
  inputClassName,
}: {
  name?: FieldPath<OrderFormValues>;
  label?: string;
  className?: string;
  inputClassName?: string;
}) {
  const { formControl } = useOrderFormContext();
  return (
    <Field className={className}>
      <FieldContent>
        <FieldLabel className="tw:text-ellipsis tw:text-nowrap">
          {label}
        </FieldLabel>
        <Controller
          control={formControl}
          name={name}
          render={({ field, fieldState }) => {
            const dateValue =
              typeof field.value === 'string'
                ? new Date(field.value)
                : field.value;
            return (
              <DatePicker
                value={dateValue as Date | undefined}
                onChange={field.onChange}
                buttonProps={{
                  disabled: field.disabled,
                  'aria-disabled': field.disabled,
                  'aria-invalid': fieldState.invalid,
                  className: cn('tw:bg-white tw:text-sm', inputClassName),
                }}
                calendarProps={{
                  defaultMonth: moment().subtract(25, 'years').toDate(),
                  hideWeekdays: true,
                }}
              />
            );
          }}
        />
      </FieldContent>
    </Field>
  );
};

OrderField.Comment = function Comment({
  className,
  inputClassName,
  control,
}: {
  className?: string;
  inputClassName?: string;
  control?: Control<any>;
}) {
  const formControl = control || useOrderFormContext().formControl;

  return (
    <Field className={className}>
      <FieldContent>
        <Controller
          control={formControl}
          name={'comment'}
          render={({ field, fieldState }) => (
            <Textarea
              {...field}
              placeholder={i18n.t('EventOrder:addComment')}
              aria-invalid={!!fieldState.error}
              className={cn('tw:bg-white tw:text-sm', inputClassName)}
            />
          )}
        />
      </FieldContent>
    </Field>
  );
};

OrderField.AddressStreet = function AddressStreet({
  name = 'address.street',
  label = i18n.t('EventOrder:addressStreet'),
  placeholder = i18n.t('EventOrder:addressStreet'),
  className,
  inputClassName,
}: {
  name?: FieldPath<OrderFormValues>;
  label?: string;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
}) {
  const { formControl } = useOrderFormContext();
  return (
    <Field className={className}>
      <FieldContent>
        <FieldLabel className="tw:text-ellipsis tw:text-nowrap">
          {label}
        </FieldLabel>
        <Controller
          control={formControl}
          name={name}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              value={field.value as string}
              placeholder={placeholder}
              aria-invalid={!!fieldState.error}
              className={cn('tw:bg-white tw:text-sm', inputClassName)}
            />
          )}
        />
      </FieldContent>
    </Field>
  );
};

// OrderField.AddressNumber = function AddressNumber({
//   name = 'address.number',
//   label = i18n.t('EventOrder:addressNumber'),
//   placeholder = i18n.t('EventOrder:addressNumber'),
//   className,
//   inputClassName,
// }: {
//   name?: FieldPath<OrderFormValues>;
//   label?: string;
//   placeholder?: string;
//   className?: string;
//   inputClassName?: string;
// }) {
//   const { form } = useOrderFormContext();
//   return (
//     <Field className={className}>
//       <FieldContent>
//         <FieldLabel className='tw:text-ellipsis tw:text-nowrap'>{label}</FieldLabel>
//         <Controller
//           control={form.control}
//           name={name}
//           render={({ field, fieldState }) => (
//             <Input
//               {...field}
//               value={field.value as string}
//               placeholder={placeholder}
//               aria-invalid={!!fieldState.error}
//               className={cn('tw:bg-white tw:text-sm', inputClassName)}
//             />
//           )}
//         />
//       </FieldContent>
//     </Field>
//   );
// };

OrderField.AddressExtra = function AddressExtra({
  name = 'address.extra',
  label = i18n.t('EventOrder:addressExtra'),
  placeholder = i18n.t('EventOrder:addressExtra'),
  className,
  inputClassName,
}: {
  name?: FieldPath<OrderFormValues>;
  label?: string;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
}) {
  const { formControl } = useOrderFormContext();
  return (
    <Field className={className}>
      <FieldContent>
        <FieldLabel className="tw:text-ellipsis tw:text-nowrap">
          {label}
        </FieldLabel>
        <Controller
          control={formControl}
          name={name}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              value={field.value as string}
              placeholder={placeholder}
              aria-invalid={!!fieldState.error}
              className={cn('tw:bg-white tw:text-sm', inputClassName)}
            />
          )}
        />
      </FieldContent>
    </Field>
  );
};

OrderField.AddressZip = function AddressZip({
  name = 'address.zip',
  label = i18n.t('EventOrder:addressZip'),
  placeholder = i18n.t('EventOrder:addressZip'),
  className,
  inputClassName,
}: {
  name?: FieldPath<OrderFormValues>;
  label?: string;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
}) {
  const { formControl } = useOrderFormContext();
  return (
    <Field className={className}>
      <FieldContent>
        <FieldLabel className="tw:text-ellipsis tw:text-nowrap">
          {label}
        </FieldLabel>
        <Controller
          control={formControl}
          name={name}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              value={field.value as string}
              placeholder={placeholder}
              aria-invalid={!!fieldState.error}
              className={cn('tw:bg-white tw:text-sm', inputClassName)}
            />
          )}
        />
      </FieldContent>
    </Field>
  );
};

OrderField.AddressCity = function AddressCity({
  name = 'address.city',
  label = i18n.t('EventOrder:addressCity'),
  placeholder = i18n.t('EventOrder:addressCity'),
  className,
  inputClassName,
}: {
  name?: FieldPath<OrderFormValues>;
  label?: string;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
}) {
  const { formControl } = useOrderFormContext();
  return (
    <Field className={className}>
      <FieldContent>
        <FieldLabel className="tw:text-ellipsis tw:text-nowrap">
          {label}
        </FieldLabel>
        <Controller
          control={formControl}
          name={name}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              value={field.value as string}
              placeholder={placeholder}
              aria-invalid={!!fieldState.error}
              className={cn('tw:bg-white tw:text-sm', inputClassName)}
            />
          )}
        />
      </FieldContent>
    </Field>
  );
};

OrderField.AddressCountry = function AddressCountry({
  name = 'address.countryCode',
  label = i18n.t('EventOrder:addressCountry'),
  className,
  inputClassName,
}: {
  name?: FieldPath<OrderFormValues>;
  label?: string;
  className?: string;
  inputClassName?: string;
}) {
  const { formControl } = useOrderFormContext();
  return (
    <Field className={className}>
      <FieldContent>
        <FieldLabel>{label}</FieldLabel>
        <Controller
          control={formControl}
          name={name}
          render={({ field, fieldState }) => {
            const f = field;
            return (
              <CountryInput
                buttonProps={{
                  'aria-invalid': !!fieldState.error,
                }}
                disabled={f.disabled}
                value={f.value as string}
                onChange={f.onChange}
                aria-invalid={!!fieldState.error}
                triggerClassName={cn('tw:bg-white', inputClassName)}
              />
            );
          }}
        />
      </FieldContent>
    </Field>
  );
};

OrderField.AddressState = function AddressState({
  name = 'address.state',
  label = i18n.t('EventOrder:addressState'),
  placeholder = i18n.t('EventOrder:addressState'),
  className,
  inputClassName,
}: {
  name?: FieldPath<OrderFormValues>;
  label?: string;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
}) {
  const { formControl } = useOrderFormContext();
  return (
    <Field className={className}>
      <FieldContent>
        <FieldLabel className="tw:text-ellipsis tw:text-nowrap">
          {label}
        </FieldLabel>
        <Controller
          control={formControl}
          name={name}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              value={field.value as string}
              placeholder={placeholder}
              aria-invalid={!!fieldState.error}
              className={cn('tw:bg-white tw:text-sm', inputClassName)}
            />
          )}
        />
      </FieldContent>
    </Field>
  );
};

OrderField.CorporateStructureInfo = function CorporateStructureInfo({
  name = 'corporateStructureInfo',
  placeholder,
  className,
  inputClassName,
}: {
  name?: FieldPath<OrderFormValues>;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
}) {
  const { formControl } = useOrderFormContext();
  return (
    <Field className={className}>
      <FieldContent>
        <Controller
          control={formControl}
          name={name}
          render={({ field, fieldState }) => (
            <Textarea
              {...field}
              value={field.value as string}
              placeholder={placeholder}
              aria-invalid={!!fieldState.error}
              className={cn('tw:bg-white tw:text-sm', inputClassName)}
            />
          )}
        />
      </FieldContent>
    </Field>
  );
};
